import { CreateDerivation, Derivation } from "@catalogue/domain/models/derivation.model";
import { Service } from "@catalogue/domain/models/service.model";
import { CreateDerivationUseCase } from "@catalogue/domain/usecases/create-derivation.usecase";
import { GetAllDerivationsUseCase } from "@catalogue/domain/usecases/get-all-derivations.usecase";
import { GetAllServicesUseCase } from "@catalogue/domain/usecases/get-all-services.usecase";
import type { DerivationFormValues } from "@catalogue/presentation/components/derivation/derivation-form.component";
import { BeneficiarySummary } from "@beneficiary/domain/models/beneficiary-summary.model";
import { GetAllBeneficiariesUseCase } from "@beneficiary/domain/usecases/get-all-beneficiaries.usecase";
import { Pagination } from "@core/domain/models/pagination";
import { LoadLayoutStore } from "@core/presentacion/component/feedback/load-layout/load-layout.store";
import { ToastManagerStore } from "@core/presentacion/component/feedback/toast-manager/toast-manager.store";
import { BaseViewModel } from "@core/presentacion/view-model/base/base.viewmodel";
import { GlobalEntity } from "@entity/domain/models/global-entity.model";
import { GetAllGlobalEntitiesUseCase } from "@entity/domain/usecases/get-all-global-entities.usecase";
import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { UserSessionRepository } from "@authentication/data/repositories/user-session.repository";
import { GetLoggedUserUseCase } from "@authentication/domain/usecases/get-logged-user.usecase";
import { toJS } from "mobx";

interface DerivationApiFilters {
    beneficiaryId?: number;
    serviceId?: number;
    entityId?: number;
    assigned_by?: number;
}

@injectable()
export class DerivationListPageViewModel extends BaseViewModel {
    @observable derivations: Derivation[] = [];
    @observable beneficiaries: BeneficiarySummary[] = [];
    @observable services: Service[] = [];
    @observable entities: GlobalEntity[] = [];

    @observable loading: boolean = false;
    @observable filtersLoading: boolean = false;
    @observable beneficiariesLoading: boolean = false;
    @observable servicesLoading: boolean = false;
    @observable entitiesLoading: boolean = false;

    @observable filters: { beneficiaryId?: number; serviceId?: number; entityId?: number } = {};
    @observable pagination: Pagination = new Pagination();
    @observable totalDerivations: number = 0;

    @observable servicesByEntity: Service[] = [];

    constructor(
        @inject(GetAllDerivationsUseCase)
        private readonly getAllDerivationsUseCase: GetAllDerivationsUseCase,
        @inject(GetAllBeneficiariesUseCase)
        private readonly getAllBeneficiariesUseCase: GetAllBeneficiariesUseCase,
        @inject(GetAllServicesUseCase)
        private readonly getAllServicesUseCase: GetAllServicesUseCase,
        @inject(GetAllGlobalEntitiesUseCase)
        private readonly getAllGlobalEntitiesUseCase: GetAllGlobalEntitiesUseCase,
        @inject(CreateDerivationUseCase)
        private readonly createDerivationUseCase: CreateDerivationUseCase,
        @inject(UserSessionRepository)
        private readonly userSessionRepository: UserSessionRepository,
        @inject(GetLoggedUserUseCase)
        private readonly getLoggedUserUseCase: GetLoggedUserUseCase,
    ) {
        super();
        makeObservable(this);
    }

    @computed
    get externalServices(): Service[] {
        // Filtra servicios de otras entidades diferentes a la del usuario actual
        return this.services.filter(service =>
            service.entity !== this.userSessionRepository.userSyncSafe.entityId
        );
    }

    @action
    async fetchDerivations(): Promise<void> {
        this.loading = true;
        const user = this.getLoggedUserUseCase.execute();

        const requestFilters: DerivationApiFilters = {
            ...this.filters,
        };

        if (user.entityId !== undefined) {
            requestFilters.assigned_by = user.entityId;
        } else if (user.isSuperAdmin()) {
            // Opcional: Si un SuperAdmin sin entidad seleccionada no debe ver nada
            // console.warn("SuperAdmin sin entidad seleccionada, no se filtra por assigned_by.");
            // O quizás quieres que vea todo, en cuyo caso no añades el filtro
        } else {
            // Usuario normal sin entityId? Error o no mostrar nada.
            console.error("Usuario no SuperAdmin sin entityId, no se puede filtrar por assigned_by.");
            // Podrías vaciar la lista y mostrar un error
            runInAction(() => {
                this.derivations = [];
                this.totalDerivations = 0;
                this.loading = false;
            });
            // Aquí podrías lanzar un toast o similar
            return;
        }

        console.log("Fetching derivations with API filters:", toJS(requestFilters));

        try {
            const result = await this.getAllDerivationsUseCase.execute(requestFilters, this.pagination);
            runInAction(() => {
                this.derivations = result.items ?? [];
                this.totalDerivations = result.total ?? 0;
            });
        } catch (error) {
            console.error("Error fetching derivations:", error);
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }

    @action
    async fetchBeneficiaries(): Promise<void> {
        this.beneficiariesLoading = true;
        try {
            const beneficiaries = await this.getAllBeneficiariesUseCase.execute();
            runInAction(() => {
                this.beneficiaries = beneficiaries;
            });
        } finally {
            runInAction(() => {
                this.beneficiariesLoading = false;
            });
        }
    }

    @action
    async fetchServices(): Promise<void> {
        this.servicesLoading = true;
        try {
            const servicesResult = await this.getAllServicesUseCase.execute();
            runInAction(() => {
                this.services = servicesResult.items || [];
            });
        } finally {
            runInAction(() => {
                this.servicesLoading = false;
            });
        }
    }

    @action
    async fetchEntities(): Promise<void> {
        this.entitiesLoading = true;
        try {
            const entities = await this.getAllGlobalEntitiesUseCase.execute();
            runInAction(() => {
                this.entities = entities;
            });
        } finally {
            runInAction(() => {
                this.entitiesLoading = false;
            });
        }
    }

    /* @action
    async createDerivation(values: DerivationFormValues): Promise<boolean> {
        const user = this.getLoggedUserUseCase.execute();

        if (!user.entityId) {
            ToastManagerStore.error("No se puede crear la derivación: El usuario actual no tiene una entidad asignada.");
            return false;
        }

        const entityIdForDerivation = values.entityId;

        const createDerivationData: CreateDerivation = {
            beneficiaryId: values.beneficiaryId,
            serviceId: values.serviceId,
            entityId: entityIdForDerivation,
            assignedById: user.entityId,

        };

        LoadLayoutStore.start();
        let success = false;
        try {
            const createdDerivation = await this.createDerivationUseCase.execute(createDerivationData);
            success = !!createdDerivation;
            if (success) {
                ToastManagerStore.success("Derivación creada correctamente");
                await this.fetchDerivations();
            } else {
                // El use case ya debería haber mostrado un error con ToastManagerStore
            }
        } catch (error) {
            console.error("Error creating derivation in ViewModel:", error);
            ToastManagerStore.error("Error inesperado al crear la derivación.");
        } finally {
            LoadLayoutStore.finish();
        }
        return success;
    } */

    @action
    viewDerivationDetails(derivationId: number): void {
        // Implementar vista detallada de derivación si es necesario
        ToastManagerStore.info("Función en desarrollo", "La vista detallada de derivaciones está en desarrollo");
    }

    @action
    setFilters(uiFilters: { beneficiaryId?: number; serviceId?: number; entityId?: number }): void {
        this.filters = uiFilters;
        this.pagination.reset();
        this.fetchDerivations();
    }

    @action
    setPagination(page: number, pageSize: number): void {
        this.pagination.page = page;
        this.pagination.pageSize = pageSize;
        this.fetchDerivations();
    }

    override async didMount(): Promise<void> {
        await super.didMount();

        this.filtersLoading = true;

        // Cargar datos iniciales concurrentemente
        await Promise.all([
            this.fetchDerivations(),
            this.fetchBeneficiaries(),
            this.fetchServices(),
            this.fetchEntities(),
        ]);

        this.filtersLoading = false;

        // Cargar los datos de la tabla
        await this.fetchDerivations();
    }

    @action
    async fetchServicesByEntity(entityId: number): Promise<void> {
        this.servicesLoading = true;
        try {
            // Fetch services filtered by the selected entity
            const services = await this.getAllServicesUseCase.execute({ entityId });
            console.log('services: ', services)
            runInAction(() => {
                // Store the filtered services in a separate property
                this.servicesByEntity = services.items || [];
                console.log('Servicios cargados:', this.externalServices); // Para depurar
            });
        } finally {
            runInAction(() => {
                this.servicesLoading = false;
            });
        }
    }
}